// imports
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { GetStaticPropsContext, GetServerSidePropsContext } from 'next';
import styles from '../../styles/ResourceOverview.module.css';
import { getResourcesOverviewRes, getArticleRes, getAllResources } from '../../helper';
import { ResourceMetadataRes, ResourcesOverviewPage, ResourcesRes } from '../../typescript/pages';
import BlogCard from '../../components/cards/blog-card';
import { SEO } from '../../helper/seo';
import Button from '../../components/ui/button';
import { useRouter } from 'next/router';
import { Breadcrumbs } from '../../components/ui/breadcrumbs';
import Image from 'next/image';
import algoliasearch from 'algoliasearch';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { motion, AnimatePresence } from 'framer-motion';
import { Chevron } from '../../components/icon/chevron';
import type { Swiper as SwiperType } from 'swiper';
import { translate } from '../../helper/translate';
import SwiperGroup from '../../components/grids/swiper-group';
import { getColorBrandSpecific } from '../../helper/utils';
import { StyleMapBannerBtn } from '../../typescript/component';

// Helper function to ensure URLs are relative
const ensureRelativeUrl = (url: string): string => {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search + urlObj.hash;
  } catch {
    // If URL parsing fails, assume it's already relative or return as is
    return url.startsWith('/') ? url : `/${url}`;
  }
};

interface Props {
  overviewData: ResourcesOverviewPage;
  resources: ResourcesRes[];
  trendingResources: ResourcesRes[];
  initialQuery: string;
  locale: string;
  resourceType?: string;
}

interface SearchResponse {
  title?: string;
  h1?: string;
  description?: string;
  summary?: string;
  sub_title: string;
  content_type?: string;
  seo?: SearchSEO;
  overview?: SearchSEO;
  youtube_url?: string;
  url: string;
  objectID: string;
  _highlightResult?: any;
  __position?: number;
  __queryID?: string;
  image?: string;
  indexed_at?: number; // Timestamp when the record was indexed (Unix timestamp in milliseconds)
  updated_at?: number; // Timestamp when the record was last updated (Unix timestamp in milliseconds)
  publish_date?: string | number; // Publish date from meta tags (ISO string or Unix timestamp)
}

interface SearchSEO {
  meta_title?: string;
  meta_description?: string;
}

const contentTypeMap: Record<string, { type: string; cta: string }> = {
  Webinar: { type: 'webinar', cta: 'View Webinar' },
  WhitePaper: { type: 'whitepaper', cta: 'View Whitepaper' },
  Article: { type: 'article', cta: 'View Article' },
  CaseStudy: { type: 'case_study', cta: 'View Case Study' },
  Video: { type: 'video', cta: 'Watch Video' },
  News: { type: 'news', cta: 'View News' },
  Event: { type: 'event', cta: 'View Event' },
};

/**
 * Convert a date value to a Unix timestamp in milliseconds
 * Handles both ISO date strings and Unix timestamps (both seconds and milliseconds)
 */
const getTimestamp = (dateValue: string | number | undefined | null): number => {
  if (!dateValue) return 0;

  // If it's already a number, assume it's a timestamp
  if (typeof dateValue === 'number') {
    // If it's less than 1e12, it's likely in seconds, convert to milliseconds
    return dateValue < 1e12 ? dateValue * 1000 : dateValue;
  }

  // If it's a string, try to parse it as an ISO date
  if (typeof dateValue === 'string') {
    const parsed = Date.parse(dateValue);
    return isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

// export async function getStaticProps(context: GetStaticPropsContext) {
export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const locale = context.locale || context.defaultLocale || 'en-us';

    const [overviewData] = await Promise.all([getResourcesOverviewRes('/resources', locale)]);

    const searchClient = algoliasearch(
      process.env.NEXT_PUBLIC_ALGOLIA_APP_ID!,
      process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY!,
    );

    const index = searchClient.initIndex(process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME!);

    const res = await index.search<SearchResponse>('', {
      filters: `locale:"${locale}" AND (content_type:"Article" OR content_type:"CaseStudy" OR content_type:"Video" OR content_type:"Webinar" OR content_type:"WhitePaper")`,
      hitsPerPage: 1000,
      clickAnalytics: true,
      // Retrieve timestamp fields for sorting
      attributesToRetrieve: ['*'], // Retrieve all attributes including indexed_at
      // Use custom ranking to prioritize recently indexed items
      ranking: ['typo', 'geo', 'words', 'filters', 'proximity', 'attribute', 'exact', 'custom'],
    });

    const defaultMetadata = {
      author: overviewData.articles_metadata_tags?.author ?? '',
      show_author: true,
      show_date: true,
      date: ""
    }

    const cleanMetadata = (metadata = {}) =>
      Object.fromEntries(
        Object.entries(metadata)
          .filter(([, value]) => value !== null && value !== undefined && value !== '')
      );

    const getMetaData = (cs: ResourcesRes | undefined) => {
      const currentMetadata = cleanMetadata(cs?.metadata_tags);
      const enMetadata = cleanMetadata(
        cs?.uid ? enResources[cs.uid]?.metadata_tags : undefined
      );

      const finalMetadata: ResourceMetadataRes = {
        ...defaultMetadata,
        ...enMetadata,
        ...currentMetadata
      }

      // Use create date if date is empty on all three levels
      // if (!finalMetadata.date) {
      //   finalMetadata.date = cs?.created_at ?? '';
      // }

      // We should force showing date as long as it's enabled in en-us
      if (enMetadata.show_date === true)
        finalMetadata.show_date = true;

      return finalMetadata;
    }

    // Fetch all resources from Contentstack directly
    const csResources = await getAllResources(locale);
    const _enResources = await getAllResources('en-us');
    const enResources: Record<string, ResourcesRes> = {};

    if (_enResources.length) {
      for (let r of _enResources) {
        enResources[r.uid] = r;
      }
    }

    const getCsResource = (objectId: string) => {
      return csResources.find((r: ResourcesRes) => {
        const cleanUrl = r.url?.replace(/^\/[a-z]{2}(?:-[a-z]{2})?\//i, "/");
        const objID = objectId?.replace(/^https?:\/\/[^/]+/, '')?.replace(/^\/[a-z]{2}(?:-[a-z]{2})?\//i, "/");

        if (cleanUrl == objID) return true;
      });
    }

    // Sort results by date (most recent first)
    // Priority: publish_date > indexed_at > updated_at
    const sortedHits = [...res.hits]

    // Filter out all articles that have en-us in the URL. Those are duplicities
    .filter(item => item?.url?.indexOf('en-us/') === -1)

    // Sort
    .sort((a, b) => {
      const csA = getCsResource(a.objectID);
      const csB = getCsResource(b.objectID);
      const metadataA = getMetaData(csA);
      const metadataB = getMetaData(csB);
      var sortDateA = metadataA.date;
      var sortDateB = metadataB.date;

      if (!sortDateA)
        sortDateA = csA?.created_at ?? '';

      if (!sortDateB)
        sortDateB = csB?.created_at ?? '';

      const aTime = getTimestamp(a.publish_date) || a.indexed_at || getTimestamp(a.updated_at) || 0;
      const bTime = getTimestamp(b.publish_date) || b.indexed_at || getTimestamp(b.updated_at) || 0;

      if (sortDateA && sortDateB) {
        return getTimestamp(sortDateB) - getTimestamp(sortDateA);
      }

      return bTime - aTime; // Descending order (newest first)
    });

    // Helper function to check if an image appears to be a default/fallback image
    const isDefaultImage = (imageUrl: string): boolean => {
      if (!imageUrl || imageUrl.trim().length === 0) return true;
      // Check for common default image patterns
      const defaultPatterns = [
        'Dynacast_Resources_Articles',
        'Resources_Articles',
        'default',
        'placeholder',
      ];
      return defaultPatterns.some((pattern) =>
        imageUrl.toLowerCase().includes(pattern.toLowerCase())
      );
    };

    type MetadataKey = keyof typeof defaultMetadata;

    // Map Algolia results to ResourcesRes format
    const allResources: ResourcesRes[] = sortedHits.map((d): ResourcesRes => {

      const mapping = contentTypeMap[d.content_type || 'Article'] || {
        type: d.content_type || '',
        cta: 'View Resource',
      };

      // ContentStack record
      const cs = getCsResource(d.objectID);

      // If the ContentStact record contains the Featured Image URL, use it instead
      if (cs?.featured_image?.url)
        d.image = cs?.featured_image?.url;

      // Check if image is missing, empty, or appears to be a default
      const hasValidImage =
        d.image && d.image.trim().length > 0 && !isDefaultImage(d.image);

      const _data = {
        uid: d.objectID,
        meta_title: d.title || '',
        title: d.h1 || '',
        description: d.description || '',
        summary: d.description || '',
        url: ensureRelativeUrl(d.url),
        featured_image: {
          url: hasValidImage && d.image ? d.image : '',
          uid: '',
          title: '',
          description: '',
          filename: '',
          content_type: '',
        },
        type: mapping.type,
        cta: mapping.cta,
        page_components: [],
        taxonomies: [],
        youtube: d.youtube_url || '',
        updated_at: '',
        created_at: cs?.created_at ?? '',
        metadata_tags: cs ? getMetaData(cs) : {},
        _content_type_uid: mapping.type,
        __queryID: d.__queryID || '',
        __position: d.__position || 0,
      }

      return _data;
    });

    let trendingResources: ResourcesRes[] = [];

    if (overviewData.trending_resources?.length > 0) {
      trendingResources = overviewData.trending_resources
      .filter(d => d.url)
      .map(d => {
        // ContentStack record
        const cs = getCsResource(d.url);

        d.metadata_tags = getMetaData(cs);
        
        return d;
      });
      
    } else {
      const trendingTypes = ['article', 'case_study', 'video', 'webinar', 'whitepaper'];
      trendingResources = trendingTypes
        .map(type => getRandomResourceByType(allResources, type))
        .filter(Boolean) as ResourcesRes[];
    }

    if (!overviewData || !Array.isArray(allResources)) {
      return {
        notFound: true,
      };
    }

    const { query } = context;

    const resourceType =
      typeof query.type === 'string' ? query.type : null;

    return {
      props: {
        overviewData,
        resources: allResources,
        trendingResources,
        locale,
        resourceType
      },
      // revalidate: 600,
    };
  } catch (err) {
    console.error('Failed to load Resources Overview', err);
    return { notFound: true };
  }
}

function getRandomResourceByType(
  resources: ResourcesRes[],
  targetType: string,
): ResourcesRes | null {
  const filtered = resources.filter(r => r._content_type_uid === targetType);
  if (filtered.length === 0) return null;
  const randomIndex = Math.floor(Math.random() * filtered.length);
  return filtered[randomIndex];
}

export default function ResourcesOverview({
  overviewData,
  resources,
  trendingResources,
  initialQuery,
  locale,
  resourceType
}: Props) {
  const [filteredResources, setFilteredResources] = useState(resources);
  const [committedSearch, setCommittedSearch] = useState('');
  const articles = filteredResources.filter(r => r._content_type_uid === 'article');
  const caseStudies = filteredResources.filter(r => r._content_type_uid === 'case_study');
  const videos = filteredResources.filter(r => r._content_type_uid === 'video');
  const webinars = filteredResources.filter(r => r._content_type_uid === 'webinar');
  const whitepapers = filteredResources.filter(r => r._content_type_uid === 'whitepaper');
  const [searchValue, setSearchValue] = useState(initialQuery || '');
  const { asPath, query, replace } = useRouter();

  const resultsWrapperRef = useRef<HTMLDivElement | null>(null);

  const getScrollOffset = () => {
    const sticky = document.querySelector(`.${styles.stickyNavWrapper}`) as HTMLElement | null;
    const header = document.querySelector('header') as HTMLElement | null;
    const stickyH = sticky ? sticky.offsetHeight : 0;
    const headerH = header ? header.offsetHeight : 0;
    return stickyH + headerH + 16;
  };

  const scrollToResultsWrapper = () => {
    const el =
      resultsWrapperRef.current ||
      (document.querySelector(`.${styles.resultsWrapper}`) as HTMLElement | null);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - getScrollOffset();
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  // make scheduled scroll cancelable
  const rafId = useRef<number | null>(null);
  const timeoutId = useRef<number | null>(null);

  const scheduleScrollToResults = () => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    if (timeoutId.current) clearTimeout(timeoutId.current);
    rafId.current = requestAnimationFrame(() => {
      timeoutId.current = window.setTimeout(() => {
        scrollToResultsWrapper();
        rafId.current = null;
        timeoutId.current = null;
      }, 40);
    });
  };

  // clean up on unmount
  useEffect(() => {
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, []);

  const tabData = useMemo(
    () =>
      [
        { key: 'all', label: 'All Resources', show: true },
        { key: 'articles', label: 'Articles', show: articles.length > 0 },
        { key: 'caseStudies', label: 'Case Studies', show: caseStudies.length > 0 },
        { key: 'videos', label: 'Videos', show: videos.length > 0 },
        { key: 'webinars', label: 'Webinars', show: webinars.length > 0 },
        { key: 'whitepapers', label: 'Whitepapers', show: whitepapers.length > 0 },
      ].filter(tab => tab.show),
    [articles.length, caseStudies.length, videos.length, webinars.length, whitepapers.length],
  );

  const getResourcesForTab = (tabKey: string): ResourcesRes[] => {
    switch (tabKey) {
      case 'articles':
        return articles;
      case 'caseStudies':
        return caseStudies;
      case 'videos':
        return videos;
      case 'webinars':
        return webinars;
      case 'whitepapers':
        return whitepapers;
      default:
        return [];
    }
  };

  const allCategories = [
    { key: 'articles', label: 'Articles', items: articles },
    { key: 'caseStudies', label: 'Case Studies', items: caseStudies },
    { key: 'videos', label: 'Videos', items: videos },
    { key: 'webinars', label: 'Webinars', items: webinars },
    { key: 'whitepapers', label: 'Whitepapers', items: whitepapers },
  ];

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [sliderLeft, setSliderLeft] = useState('0px');
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeTab, setActiveTab] = useState<
    'all' | 'articles' | 'caseStudies' | 'videos' | 'webinars' | 'whitepapers' | 'search'
  >('all');
  const isAllResources = activeTab === 'all';

  useEffect(() => {
    const currentTab = tabRefs.current[activeTabIndex];
    if (currentTab) {
      setSliderLeft(`${currentTab.offsetLeft}px`);
    }
  }, [activeTabIndex]);

  const [isSticky, setIsSticky] = useState(false);
  const stickyTriggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 1 },
    );
    const el = stickyTriggerRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'all' && getResourcesForTab(activeTab).length === 0) {
      setActiveTab('all');
      setActiveTabIndex(0);
    }
  }, [
    activeTab,
    articles.length,
    caseStudies.length,
    videos.length,
    webinars.length,
    whitepapers.length,
  ]);

  const handleTabSelect = (tabKey: string) => {
    const idx = tabKeyToIndex[tabKey];
    if (idx !== undefined) {
      handleTabClick(idx);
    }
  };

  const handleTabClick = (index: number) => {
    const selectedKey = tabData[index].key as typeof activeTab;

    // tab click
    replace({ pathname: '/resources', query: { ...query, type: selectedKey } }, undefined, {
      shallow: true,
    });
    setActiveTab(selectedKey);
    setActiveTabIndex(index);
    scheduleScrollToResults();
  };

  const tabKeyToIndex = useMemo(
    () =>
      tabData.reduce(
        (acc, tab, idx) => {
          acc[tab.key] = idx;
          return acc;
        },
        {} as Record<string, number>,
      ),
    [tabData],
  );

  // only react to real query.type changes
  const lastTypeRef = useRef<string | null>(null);

  useEffect(() => {
    const tabFromUrl = typeof query.type === 'string' ? query.type : 'all';
    if (tabFromUrl === lastTypeRef.current) return;

    const idx = tabKeyToIndex[tabFromUrl];
    if (idx !== undefined) {
      setActiveTab(tabFromUrl as typeof activeTab);
      setActiveTabIndex(idx);
      if (tabFromUrl !== 'all') scheduleScrollToResults();
    } else {
      setActiveTab('all');
      setActiveTabIndex(0);
    }

    lastTypeRef.current = tabFromUrl;
  }, [query.type, tabKeyToIndex]);

  const getSearchButton = (width: string, height: string, fontSize: string) => (
    <Button
      text={translate('search', locale ?? 'en-us')}
      showIcon
      icon={getSearchIcon()}
      onClick={handleSearch}
      propHeight={height}
      propWidth={width}
      propFontSize={fontSize}
      propBackgroundColor="var(--brand-primary-500)"
      propBorderColor="var(--brand-primary-500)"
      propTextColor="var(--white)"
      propHoverText="var(--brand-primary-500)"
      propHoverBackgroundColor="var(--white)"
    />
  );

  const getSearchIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="currentColor"
      style={{ marginLeft: 'var(--xs)' }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.5 4.25042C6.50544 4.25042 5.55161 4.64551 4.84835 5.34877C4.14509 6.05203 3.75 7.00586 3.75 8.00042C3.75 8.99498 4.14509 9.94881 4.84835 10.6521C5.55161 11.3553 6.50544 11.7504 7.5 11.7504C8.49456 11.7504 9.44839 11.3553 10.1517 10.6521C10.8549 9.94881 11.25 8.99498 11.25 8.00042C11.25 7.00586 10.8549 6.05203 10.1517 5.34877C9.44839 4.64551 8.49456 4.25042 7.5 4.25042ZM2.25 8.00042C2.25 7.16886 2.44753 6.3492 2.82632 5.60893C3.20512 4.86866 3.75434 4.22896 4.42875 3.7425C5.10317 3.25604 5.8835 2.93674 6.70548 2.8109C7.52745 2.68505 8.36757 2.75626 9.15664 3.01866C9.94571 3.28105 10.6612 3.72713 11.2441 4.32017C11.827 4.9132 12.2607 5.63621 12.5095 6.42968C12.7583 7.22314 12.8151 8.06435 12.6751 8.88405C12.5351 9.70374 12.2025 10.4785 11.7045 11.1444L15.5303 14.9694C15.671 15.11 15.7501 15.3008 15.7502 15.4998C15.7502 15.6987 15.6713 15.8896 15.5306 16.0303C15.39 16.171 15.1992 16.2501 15.0003 16.2502C14.8013 16.2503 14.6105 16.1713 14.4697 16.0307L10.6447 12.2057C9.86439 12.7894 8.93694 13.1444 7.96625 13.231C6.99556 13.3175 6.01994 13.1322 5.14861 12.6957C4.27728 12.2592 3.54463 11.5888 3.0327 10.7596C2.52077 9.93034 2.24976 8.97496 2.25 8.00042Z"
      />
    </svg>
  );

  const handleSearch = async () => {
    const q = searchValue.trim();
    setCommittedSearch(q);

    if (!q) {
      setFilteredResources(resources);
      setActiveTab('all');
      scheduleScrollToResults(); // add this line
      return;
    }

    performSearch(q, 1);
    scheduleScrollToResults();
  };

  const performSearch = async (q: string, _pageNum: number) => {
    try {
      const searchClient = algoliasearch(
        process.env.NEXT_PUBLIC_ALGOLIA_APP_ID!,
        process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY!,
      );

      const index = searchClient.initIndex(process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME!);

      const res = await index.search<SearchResponse>(q, {
        filters: `locale:"${locale}" AND (content_type:"Article" OR content_type:"CaseStudy" OR content_type:"Video" OR content_type:"Webinar" OR content_type:"WhitePaper")`,
        hitsPerPage: 1000,
        clickAnalytics: true,
        // Retrieve timestamp fields for sorting
        attributesToRetrieve: ['*'], // Retrieve all attributes including indexed_at
        // Use custom ranking to prioritize recently indexed items
        ranking: ['typo', 'geo', 'words', 'filters', 'proximity', 'attribute', 'exact', 'custom'],
      });

      // Sort results by date (most recent first)
      // Priority: publish_date > indexed_at > updated_at
      const sortedHits = [...res.hits].sort((a, b) => {
        const aTime =
          getTimestamp(a.publish_date) || a.indexed_at || getTimestamp(a.updated_at) || 0;
        const bTime =
          getTimestamp(b.publish_date) || b.indexed_at || getTimestamp(b.updated_at) || 0;
        return bTime - aTime; // Descending order (newest first)
      });

      const updatedResources: ResourcesRes[] = sortedHits.map((d): ResourcesRes => {
        const mapping = contentTypeMap[d.content_type || 'Article'] || {
          type: d.content_type || '',
          cta: 'View Resource',
        };

        return {
          uid: d.objectID,
          title: d.h1 || '',
          meta_title: d.title || '',
          description: d.description || '',
          summary: d.description || '',
          url: ensureRelativeUrl(d.url),
          featured_image: {
            url: d.image || '',
            uid: '',
            title: '',
            description: '',
            filename: '',
            content_type: '',
          },
          type: mapping.type,
          cta: mapping.cta,
          page_components: [],
          taxonomies: [],
          updated_at: '',
          created_at: '',
          metadata_tags: {
            author: ''
          },
          _content_type_uid: mapping.type,
          __queryID: d.__queryID || '',
          __position: d.__position || 0,
        };
      });

      setFilteredResources(updatedResources);
      setActiveTab('search');
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchValue(val);

    if (val.trim() === '') {
      // Reset when search box is cleared
      setCommittedSearch('');
      setFilteredResources(resources);
      setActiveTab('all');
      setIsSticky(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const pathnames = asPath.split('/').filter(x => x);

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.07,
      },
    },
  };

  const activeTabLabel = tabData.find(tab => tab.key === activeTab)?.label || '';
  const swiperRef = useRef<SwiperType | null>(null);
  const prevTrendingRef = useRef<HTMLButtonElement | null>(null);
  const nextTrendingRef = useRef<HTMLButtonElement | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const [swiperReady, setSwiperReady] = useState(false);

  const headerImageUrl = overviewData?.header_background?.url;
  const headerImageAlt = overviewData?.header_background?.description || 'Banner Image';
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // run once
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update Swiper navigation and pagination refs after mount (React 19 compatibility)
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.params && swiperReady) {
      // Update navigation refs after mount
      if (typeof swiperRef.current.params.navigation === 'object') {
        swiperRef.current.params.navigation.prevEl = prevTrendingRef.current;
        swiperRef.current.params.navigation.nextEl = nextTrendingRef.current;
        swiperRef.current.navigation.destroy();
        swiperRef.current.navigation.init();
        swiperRef.current.navigation.update();
      }

      // Update pagination refs after mount
      if (typeof swiperRef.current.params.pagination === 'object') {
        swiperRef.current.params.pagination.el = paginationRef.current;
        swiperRef.current.pagination.destroy();
        swiperRef.current.pagination.init();
        swiperRef.current.pagination.update();
      }
      
      // Force Swiper to recalculate slides (important for SSR/production)
      swiperRef.current.update();
      if (swiperRef.current.slides) {
        swiperRef.current.updateSlides();
        swiperRef.current.updateSlidesClasses();
      }
    }
  }, [swiperReady]);

  // Additional update after a short delay to ensure CSS is loaded (production fix)
  useEffect(() => {
    if (swiperReady && swiperRef.current) {
      const timeoutId = setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.update();
          if (swiperRef.current.slides) {
            swiperRef.current.updateSlides();
            swiperRef.current.updateSlidesClasses();
          }
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [swiperReady]);

  return (
    <>
      <SEO
        meta_title={overviewData?.seo?.meta_title}
        meta_description={overviewData?.seo?.meta_description}
        keywords={overviewData?.seo?.keywords}
        share_image={overviewData?.header_background?.url}
        currentLocale={locale}
        baseUrl={process.env.NEXT_PUBLIC_HOSTED_URL || ''}
        allowedParamsInCanonicalHrefLang={{
          type: Object.entries(tabData.filter(i => i.key === resourceType)).length ? resourceType : undefined
        }}
      />

      <div className={styles.outerWrapper}>
        <div className={styles.innerWrapper}>
          <div className={styles.imageContainer}>
            {headerImageUrl ? (
              <Image
                key={headerImageUrl}
                className={styles.imageIcon}
                src={`${headerImageUrl}?width=1440&auto=webp&fit=crop,smart`}
                alt={headerImageAlt}
                width={1440}
                height={440}
                loading="lazy"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1440px"
                placeholder="blur"
                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
              />
            ) : null}
            <div className={styles.imageOverlay}></div>
          </div>
          <div className={styles.headerWrapper}>
            <div className={styles.breadcrumbs}>
              <Breadcrumbs
                pathnames={pathnames}
                title={overviewData.header_title}
                key={pathnames.join('/')}
                isDark={true}
              />
            </div>
            <h1 className={styles.search}>{overviewData.header_title}</h1>
            <div className={styles.searchInputBar}>
              <input
                className={styles.searchInput}
                value={searchValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                id="inputSearch"
              />
              <div id="searchButton" className={styles.searchButton}>
                {getSearchButton('7.125rem', '1.75rem', '0.8125rem')}
              </div>
              <div id="searchButtonMobile" className={styles.searchButtonMobile}>
                {getSearchButton('7.6525rem', '2.625rem', '1rem')}
              </div>
            </div>
          </div>
          {!committedSearch && (
            <>
              <div ref={stickyTriggerRef} className={styles.stickyTrigger}></div>
              <div className={`${styles.stickyNavWrapper} ${isSticky ? styles.isStuck : ''}`}>
                <div className={styles.navigationWrapper}>
                  <Swiper
                    slidesPerView="auto"
                    freeMode={true}
                    grabCursor={true}
                    simulateTouch={true}
                    spaceBetween={32}
                    className={styles.navigationSwiper}
                  >
                    {tabData.map((tab, index) => (
                      <SwiperSlide key={tab.key} className={styles.swiperSlide}>
                        <div
                          ref={el => {
                            tabRefs.current[index] = el;
                          }}
                          className={`${styles.navSelector} ${index === activeTabIndex ? styles.navSelectorActive : ''}`}
                          onClick={() => handleTabClick(index)}
                        >
                          {translate(tab.label.toLowerCase(), locale || 'en-us')}
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </>
          )}
          {trendingResources.length > 0 && !committedSearch && (
            <div className={`${styles.trendingGroup} ${styles.fullWidthSection}`}>
              <motion.div
                className={`resource-overview-trending ${styles.trendingGroupInternal}`}
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: 0.2,
                    },
                  },
                }}
              >
                <motion.h3
                  className={styles.light}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  {translate('Trending', locale || 'en-us')}
                </motion.h3>

                <Swiper
                  modules={[Navigation, Pagination]}
                  spaceBetween={16}
                  slidesPerView="auto"
                  slidesPerGroup={1}
                  loop={trendingResources.length > 3}
                  observer={true}
                  observeParents={true}
                  observeSlideChildren={true}
                  updateOnWindowResize={true}
                  className={styles.horizontalSwiper}
                  onInit={(swiper: SwiperType) => {
                    // Force update on init to ensure proper calculation
                    requestAnimationFrame(() => {
                      swiper.update();
                      if (swiper.slides) {
                        swiper.updateSlides();
                        swiper.updateSlidesClasses();
                      }
                    });
                  }}
                  onAfterInit={(swiper: SwiperType) => {
                    // Additional update after full initialization
                    requestAnimationFrame(() => {
                      if (swiper) {
                        swiper.update();
                        if (swiper.slides) {
                          swiper.updateSlides();
                          swiper.updateSlidesClasses();
                        }
                      }
                    });
                  }}
                  onResize={(swiper: SwiperType) => {
                    // Recalculate on resize
                    if (swiper) {
                      swiper.update();
                      if (swiper.slides) {
                        swiper.updateSlides();
                        swiper.updateSlidesClasses();
                      }
                    }
                  }}
                  pagination={{
                    el: paginationRef.current,
                    clickable: false,
                    dynamicBullets: true,
                  }}
                  navigation={{
                    prevEl: prevTrendingRef.current,
                    nextEl: nextTrendingRef.current,
                  }}
                  onSwiper={s => {
                    swiperRef.current = s;
                    setSwiperReady(true);
                  }}
                >
                  {trendingResources.map((item, index) => (
                    <SwiperSlide key={index} className={styles.slide}>
                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <BlogCard
                          blog_post={item}
                          component_type="Resource List"
                          size="wide"
                          number={index + 1}
                        />
                      </motion.div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </motion.div>
              <div className={styles.swiperNavigationWrapper}>
                <Chevron ref={prevTrendingRef} direction="left" variant="light" />
                <div ref={paginationRef} className="light"></div>
                <Chevron ref={nextTrendingRef} direction="right" variant="light" />
              </div>
            </div>
          )}

          <div className={styles.resourceWrapper}>
            <div className={styles.resultsWrapper} ref={resultsWrapperRef}>
              <AnimatePresence mode="wait">
                {committedSearch ? (
                  <div className={styles.motionWrapper}>
                    <motion.h3
                      key="search-heading"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className={styles.groupHeader}
                    >
                      {translate('search_results_for', locale)}: “{committedSearch}”
                      {committedSearch && (
                        <a
                          className={styles.clearSearchButton}
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            setSearchValue('');
                            setCommittedSearch('');
                            setFilteredResources(resources);
                            setActiveTab('all');
                            scheduleScrollToResults();
                            setIsSticky(false);
                          }}
                        >
                          {translate('clear_all_filters', locale || 'en-us')}
                        </a>
                      )}
                    </motion.h3>

                    <motion.div
                      key="search-results"
                      variants={{
                        visible: {
                          transition: {
                            staggerChildren: 0.05,
                          },
                        },
                      }}
                      initial="hidden"
                      animate="visible"
                      className={styles.resourceGrid}
                    >
                      {filteredResources.length > 0 ? (
                        filteredResources.map((item, index) => (
                          <motion.div
                            key={index}
                            variants={{
                              hidden: { opacity: 0, y: 10 },
                              visible: { opacity: 1, y: 0 },
                            }}
                          >
                            <BlogCard key={index} blog_post={item} component_type="Resource List" />
                          </motion.div>
                        ))
                      ) : (
                        <p>No results found.</p>
                      )}
                    </motion.div>
                  </div>
                ) : isAllResources ? (
                  <motion.div
                    key="all-resources"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className={styles.allResources}
                  >
                    {allCategories.map(({ key, label, items }) => {
                      return items.length > 0 ? (
                        <React.Fragment key={key}>
                          <div id={`section-${key}`} className={`resource-overview-section ${styles.headerWrap}`}>
                            <motion.h3
                              initial={{ opacity: 0, y: -20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.6 }}
                              className={styles.groupHeader}
                            >
                              {translate(label.toLowerCase(), locale, label)}
                            </motion.h3>
                            {!isMobile && (
                              <Button
                                onClick={() => {
                                  handleTabSelect(key);
                                  scheduleScrollToResults();
                                }}
                                propTextColor={getColorBrandSpecific(
                                  StyleMapBannerBtn.BRAND_SPECIFIC,
                                )}
                                propBorderColor={getColorBrandSpecific(
                                  StyleMapBannerBtn.BRAND_SPECIFIC,
                                )}
                                propHoverBackgroundColor={getColorBrandSpecific(
                                  StyleMapBannerBtn.BRAND_SPECIFIC,
                                )}
                                propHoverText="white"
                                showIcon
                                propWidth="300px"
                                text={translate(
                                  'view_all_' + label.toLowerCase().replace(/\s+/g, '_'),
                                  locale,
                                  'View all ' + label,
                                )}
                              />
                            )}

                            <SwiperGroup
                              keyId={key}
                              label={translate(label.toLowerCase(), locale)}
                              items={items}
                              locale={locale}
                            />
                            {isMobile && (
                              <div className={styles.mobileViewAllWrapper}>
                                <Button
                                  onClick={() => {
                                    handleTabSelect(key);
                                    scheduleScrollToResults();
                                  }}
                                  propTextColor={getColorBrandSpecific(
                                    StyleMapBannerBtn.BRAND_SPECIFIC,
                                  )}
                                  propBorderColor={getColorBrandSpecific(
                                    StyleMapBannerBtn.BRAND_SPECIFIC,
                                  )}
                                  propHoverBackgroundColor={getColorBrandSpecific(
                                    StyleMapBannerBtn.BRAND_SPECIFIC,
                                  )}
                                  propHoverText="white"
                                  showIcon
                                  propWidth="100%"
                                  text={translate(
                                    'view_all_' + label.toLowerCase().replace(/\s+/g, '_'),
                                    locale,
                                    'View all ' + label,
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        </React.Fragment>
                      ) : null;
                    })}
                  </motion.div>
                ) : (
                  <div className={styles.motionWrapper}>
                    <div>
                      <motion.h3
                        key={activeTabLabel}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className={styles.groupHeader}
                      >
                        {translate(activeTabLabel.toLowerCase(), locale)}
                      </motion.h3>
                    </div>
                    <motion.div
                      key={activeTab}
                      variants={{
                        visible: {
                          transition: {
                            staggerChildren: 0.05,
                          },
                        },
                      }}
                      initial="hidden"
                      animate="visible"
                      className={styles.resourceGrid}
                    >
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={`updated-results-${activeTab}-${searchValue}`}
                          variants={containerVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className={styles.resourceGrid}
                        >
                          {getResourcesForTab(activeTab).map((item, index) => (
                            <motion.div
                              key={index}
                              variants={{
                                hidden: { opacity: 0, y: 10 },
                                visible: { opacity: 1, y: 0 },
                              }}
                            >
                              <BlogCard
                                key={index}
                                blog_post={item}
                                component_type="Resource List"
                              />
                            </motion.div>
                          ))}
                        </motion.div>
                      </AnimatePresence>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
